/**
 * Dashboard App Component
 * 
 * @package TopBuddy
 */

import { createPortal, useState, useEffect } from '@wordpress/element';
import { TabPanel, Button, NoticeList } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import NotificationBarsList from './components/NotificationBarsList';
import Settings from './components/Settings';

const App = () => {
	const [headerActionsContainer, setHeaderActionsContainer] = useState(null);
	const [isPro, setIsPro] = useState(false);
	const [canManageOptions, setCanManageOptions] = useState(false);
	const [roles, setRoles] = useState({});
	const [accessRoles, setAccessRoles] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [notices, setNotices] = useState([]);

	const tabs = [
		{
			name: 'notification-bars',
			title: __('Notification Bars', 'topbuddy'),
			className: 'topbuddy-tab-notification-bars',
		},
	];

	if (canManageOptions) {
		tabs.push({
			name: 'settings',
			title: __('Settings', 'topbuddy'),
			className: 'topbuddy-tab-settings',
		});
	}

	useEffect(() => {
		// Query DOM after component mount
		const container = document.querySelector('.topbuddy-header-actions');
		setHeaderActionsContainer(container);
	}, []);

	useEffect(() => {
		const fetchIsPro = async () => {
			try {
				const data = await apiFetch({
					path: '/topbuddy/v1/notification-bars',
					method: 'GET',
				});
				setIsPro(data.isPro || false);
				setCanManageOptions(data.canManageOptions || false);
				setRoles(data.roles || {});
				setAccessRoles(data.accessRoles || []);
			} catch (err) {
				console.error('Error fetching isPro:', err);
			}
		};

		fetchIsPro();
	}, []);

	const handleAddNew = () => {
		window.location.href = `${window.topbuddyDashboard.newPostUrl}?post_type=topbuddy_nb`;
	};

	const handleSaveSettings = async () => {
		setIsSaving(true);
		try {
			await apiFetch({
				path: '/topbuddy/v1/settings',
				method: 'POST',
				data: { accessRoles },
			});
			setNotices([
				{
					id: 'settings-saved',
					content: __('Settings saved successfully.', 'topbuddy'),
					status: 'success',
					isDismissible: true,
				},
			]);
		} catch (err) {
			setNotices([
				{
					id: 'settings-error',
					content: err.message || __('An error occurred while saving settings.', 'topbuddy'),
					status: 'error',
					isDismissible: true,
				},
			]);
		} finally {
			setIsSaving(false);
		}
	};

	const removeNotice = (id) => {
		setNotices((prevNotices) => prevNotices.filter((notice) => notice.id !== id));
	};

	return (
		<div className="topbuddy-dashboard-container">
			{headerActionsContainer && createPortal(
				<Button variant="primary" onClick={handleAddNew}>
					{__('+ Add New Notification Bar', 'topbuddy')}
				</Button>,
				headerActionsContainer
			)}
			<TabPanel
				className="topbuddy-tab-panel"
				activeClass="is-active"
				tabs={tabs}
			>
				{(tab) => (
					<div className="topbuddy-tab-content">
						{tab.name === 'notification-bars' && <NotificationBarsList />}
						{tab.name === 'settings' && (
							<Settings
								isPro={isPro}
								roles={roles}
								accessRoles={accessRoles}
								onAccessRolesChange={setAccessRoles}
								onSave={handleSaveSettings}
								isSaving={isSaving}
								notices={notices}
								onRemoveNotice={removeNotice}
							/>
						)}
					</div>
				)}
			</TabPanel>
		</div>
	);
};

export default App;

